import { ReactNode, createContext, useContext } from 'react';

import { Card, CardContent, Stack, StackProps } from '@mui/material';
import cx from 'classnames';

import styles from './CardGridItem.module.scss';

interface CardGridItemProps {
  className?: string;
  children: ReactNode;
  severity?: 'error' | 'success' | 'warning' | 'info';
  autoTopMarginOn?: 'top' | 'middle' | 'bottom';
}

interface CardGridItemSectionProps extends StackProps {
  children: ReactNode;
}

const CardGridItemContext = createContext<{
  autoTopMarginOn?: 'top' | 'middle' | 'bottom';
}>({});

const CardGridItemRoot = ({
  children,
  severity,
  className,
  autoTopMarginOn = 'middle',
}: CardGridItemProps) => {
  return (
    <CardGridItemContext.Provider value={{ autoTopMarginOn }}>
      <Card
        className={cx(styles.Card, className, {
          [styles.Error]: severity === 'error',
          [styles.Success]: severity === 'success',
          [styles.Warning]: severity === 'warning',
          [styles.Info]: severity === 'info',
        })}
      >
        <CardContent className={styles.CardContent}>
          <Stack gap={2} className={styles.CardContentInner}>
            {children}
          </Stack>
        </CardContent>
      </Card>
    </CardGridItemContext.Provider>
  );
};

const Top = ({ children, className, ...props }: CardGridItemSectionProps) => {
  const { autoTopMarginOn } = useContext(CardGridItemContext);
  return (
    <Stack
      gap={1}
      className={cx(className, {
        [styles.CardContentAutoMarginTop]: autoTopMarginOn === 'top',
      })}
      {...props}
    >
      {children}
    </Stack>
  );
};

const Middle = ({
  children,
  className,
  ...props
}: CardGridItemSectionProps) => {
  const { autoTopMarginOn } = useContext(CardGridItemContext);
  return (
    <Stack
      gap={2}
      className={cx(className, {
        [styles.CardContentAutoMarginTop]: autoTopMarginOn === 'middle',
      })}
      {...props}
    >
      {children}
    </Stack>
  );
};

const Bottom = ({
  children,
  className,
  ...props
}: CardGridItemSectionProps) => {
  const { autoTopMarginOn } = useContext(CardGridItemContext);
  return (
    <Stack
      gap={1}
      className={cx(className, {
        [styles.CardContentAutoMarginTop]: autoTopMarginOn === 'bottom',
      })}
      {...props}
    >
      {children}
    </Stack>
  );
};

export const CardGridItem = Object.assign(CardGridItemRoot, {
  Top,
  Middle,
  Bottom,
});
